<template>
  <div class="bg-gray-50 py-6 pb-24 space-y-6" dir="rtl">
    <div class="max-w-2xl mx-auto px-4 space-y-6">
    
    <!-- Date Range -->
    <div class="bg-white rounded-xl p-5 border border-gray-200">
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-bold text-gray-700 mb-3">از تاریخ</label>
          <date-picker v-model="start_date" />
        </div>
        <div>
          <label class="block text-sm font-bold text-gray-700 mb-3">تا تاریخ</label>
          <date-picker v-model="end_date" />
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="request_items.length === 0" class="flex flex-col items-center justify-center py-20">
      <div class="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <p class="text-gray-500 text-sm">نتیجه‌ای یافت نشد</p>
    </div>

    <!-- Referred Leave Requests List -->
    <div v-else class="space-y-3">
      <div 
        v-for="item in request_items"
        :key="item.Id"
        @click="get_leave_images(item)"
        :class="[
          'bg-white rounded-xl border p-5 cursor-pointer transition-all duration-200',
          card_style(item) === 'blue' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:shadow-md'
        ]"
      >
        <!-- Header -->
        <div class="flex items-center gap-3 mb-4">
          <div class="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
            <svg class="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div class="w-full">
            <p class="text-xs text-gray-500 mb-1">شماره درخواست</p>
            <h3 class="text-base font-bold text-gray-900">{{ item.LeaveCode }}</h3>
          </div>
          <div v-if="card_style(item) === 'blue'" class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>

        <!-- Request Info -->
        <div class="grid grid-cols-2 gap-3 mb-3">
          <div class="p-3 bg-gray-50 rounded-lg">
            <p class="text-xs text-gray-600 mb-1">درخواست کننده</p>
            <p class="text-sm font-bold text-gray-900">{{ item.UserName }}</p>
          </div>
          <div class="p-3 bg-gray-50 rounded-lg">
            <p class="text-xs text-gray-600 mb-1">نوع درخواست</p>
            <p class="text-sm font-bold text-gray-900">{{ morakhasi_type_list.filter(x=>x.value==item.LeaveType)[0].label }}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3 mb-3">
          <div class="p-3 bg-gray-50 rounded-lg">
            <p class="text-xs text-gray-600 mb-1">جانشین</p>
            <p class="text-sm font-bold text-gray-900">{{ item.SuccessorName }}</p>
          </div>
          <div class="p-3 bg-gray-50 rounded-lg">
            <p class="text-xs text-gray-600 mb-1">از تاریخ</p>
            <p class="text-sm font-bold text-gray-900">{{ get_date_time(item.FromDate) }}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3 mb-3">
          <div class="p-3 bg-gray-50 rounded-lg">
            <p class="text-xs text-gray-600 mb-1">تا تاریخ</p>
            <p class="text-sm font-bold text-gray-900">{{ get_date_time(item.ToDate) }}</p>
          </div>
        </div>

        <!-- Description -->
        <div v-if="item.LeaveDesc" class="p-3 bg-gray-50 rounded-lg mb-3">
          <p class="text-xs text-gray-600 mb-1">توضیحات</p>
          <p class="text-sm text-gray-900">{{ item.LeaveDesc }}</p>
        </div>

        <!-- Status Badge -->
        <div class="flex items-center justify-center">
          <div 
            v-if="item.FirstAccepted==''"
            class="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-bold"
          >
            در انتظار تایید
          </div>
          <div 
            v-if="item.FirstAccepted!='' && item.SecondAccepted==''"
            class="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-bold"
          >
            در انتظار تاییدیه دوم
          </div>
          <div 
            v-if="item.FirstAccepted!='' && item.SecondAccepted!=''"
            class="px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-bold"
          >
            تایید شده
          </div>
        </div>

      </div>
    </div>

    <infinite-loading @infinite="infiniteHandler" v-if="show_loading">
      <div slot="no-more">{{ $t('no_records') }}</div>
    </infinite-loading>

    <!-- Status Dialog -->
    <div v-if="morakhasi_status_dialog" class="fixed inset-0 bg-black bg-opacity-50 z-999 flex items-start justify-center p-4 pt-16">
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-90vh overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="px-6 py-4 bg-gradient-to-r from-teal-600 to-teal-700 flex items-center justify-between flex-shrink-0">
          <h2 class="text-lg font-bold text-white w-full">{{ $t('information') }}</h2>
          <button 
            @click="morakhasi_status_dialog = false"
            class="w-8 h-8 rounded-lg bg-white bg-opacity-20 hover:bg-white hover:bg-opacity-30 flex items-center justify-center transition-colors"
          >
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <!-- Body -->
        <div class="px-6 py-6 overflow-y-auto flex-1 space-y-4">
          <!-- Status Select -->
          <div>
            <label class="block text-sm font-bold text-gray-700 mb-3">{{ $t('NewStatusChangeStatus') }}</label>
            <div class="relative">
              <select 
                v-model="selected_status_for_change"
                class="morakhasi-modal-field morakhasi-modal-select"
              >
                <option 
                  v-for="status in status_list"
                  :key="status.value"
                  :value="status.value"
                >
                  {{ status.label }}
                </option>
              </select>
              <div class="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                </svg>
              </div>
            </div>
          </div>

          <!-- Summary Textarea -->
          <div>
            <label class="block text-sm font-bold text-gray-700 mb-3">{{ $t('summary') }}</label>
            <textarea
              v-model="status_summary"
              rows="3"
              class="morakhasi-modal-field morakhasi-modal-textarea"
              placeholder="توضیحات را وارد کنید..."
            ></textarea>
          </div>

          <!-- Images -->
          <div v-if="selected_leave_images.length > 0">
            <label class="block text-sm font-bold text-gray-700 mb-3">تصاویر</label>
            <div class="grid grid-cols-2 gap-3">
              <img 
                v-for="img in selected_leave_images" 
                :key="img.ImagePath"
                @click="open_image(img.ImagePath)"
                :src="img.ImagePath"
                class="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity border border-gray-200"
              />
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button 
            @click="morakhasi_status_dialog = false"
            class="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-all duration-200"
          >
            {{ $t('close') }}
          </button>
          <button 
            @click="go_to_finalize()"
            class="flex-1 px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-medium rounded-lg transition-all duration-200"
          >
            ثبت وضعیت
          </button>
        </div>
      </div>
    </div>

    <!-- Add Request Button -->
    <button 
      @click="go_to_add_morakhasi()"
      class="w-full bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white py-4 px-6 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2"
    >
      درخواست مرخصی
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
      </svg>
    </button>

    </div>
  </div>
</template>
<script>
  import InfiniteLoading from 'vue-infinite-loading'

  import Treeselect, {ASYNC_SEARCH} from '@riophae/vue-treeselect'
  // import the styles
  import '@riophae/vue-treeselect/dist/vue-treeselect.css'

  import {bus} from '../../main'
  import {longClickDirective} from 'vue-long-click'
  import Vue from 'vue'
  import mapboxgl from "mapbox-gl";

  import VueSignaturePad from 'vue-signature-pad'
  Vue.use(VueSignaturePad)

  const longClickInstance = longClickDirective({delay: 500, interval: 5000})
  Vue.directive('longclick', longClickInstance)
  const simulateAsyncOperation = fn => {
    setTimeout(fn, 200)
  }
  import moment from 'moment-jalaali'

  export default {
    props: [
      'passing_data'
    ],

    data() {
      return {
        start_date:'',
        end_date:'',
        morakhasi_type_list:[
          {label:'انتخاب کنید',value:0},
          {label:'ساعتی ',value:1},
          {label:'استحقاقی ',value:2},
          {label:'استعلاجی',value:3},
          {label:'دون حقوق',value:4}],
        signature_options: {
          dotSize: (0.5 + 2.5) / 2,
          minWidth: 0.5,
          maxWidth: 2.5,
          throttle: 16,
          minDistance: 5,
          backgroundColor: 'rgb(255,255,255)',
          penColor: 'black',
          velocityFilterWeight: 0.7,
          onBegin: () => {
          },
          onEnd: () => {
          }
        },
        dialog_model: false,
        status_summary: '',
        active_tab: 0,
        selected_status_for_change: 0,
        morakhasi_status_dialog: false,
        selected_product_info: {},
        list: [],
        status_list: [{label:'تایید درخواست',value:1},{label:'رد درخواست',value:2}],
        errors: [],
        post_items: [],
        selected_post: 0,
        dialog_factor: false,
        enable_finalize: false,
        selected_branch: 0,
        request_items: [],
        last_request_items: [],
        request_statuses: [],
        show_loading: false,
        page: 0,
        request_summary: '',
        selected_request: null,
        selected_user: null,
        disable_click: false,
        category_items: [],
        selected_category: 0,
        selected_leave_images:[],
        selected_leave_id:-1,
        normalizer_requests(node) {
          return {
            id: node.Id,
            label: node.Title,
          }
        },
        normalizer_persons(node) {
          return {
            id: node.Id,
            label: node.UserName,
          }
        },
        first: true,
      }
    },
    components: {
      InfiniteLoading,
      Treeselect
    },
    watch: {
      selected_category: function (val) {
        this.selected_request = null
        this.selected_user = null
      },
      start_date: function () {
        this.get_pre_requests(0)
      },
      end_date: function () {
        this.get_pre_requests(0)
      },

    },
    mounted() {
      this.start_date = moment().utc().add(-7, 'day').format('jYYYY/jMM/jDD');
      this.end_date = moment().utc().format('jYYYY/jMM/jDD');

      this.get_pre_requests();
    },

    methods: {
      get_date_time(date){
        return moment(date).format('jYYYY/jM/jD HH:mm');
      },
      get_tab_style(index) {
        if (index == this.active_tab) {
          return 'blue'
        } else {
          return 'white'
        }
      },
      set_active_tab(index) {
        this.active_tab = index;
      },
      go_to_add_morakhasi(){
        this.$router.push({name:'AddMorakhasi'})
      },
      get_pre_requests($state) {
        // this.request_items = [];
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          UserId: user_id,
          PersonId: user_id,
          fDate:this.start_date,
          tDate:this.end_date,
          // Count: this.page,
        }
        // GetLeaveDataRpt => UserId PersonId fDate tDate
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/GetLeaveDataRpt', send_data, {})
          .then(response => {
            self.request_items = []
            if (response.data.error == 0) {
              self.request_items=(JSON.parse(response.data.items))
              console.log("my status", self.request_items)

            }
          }).catch(e => {
          this.errors.push(e)
        })
      },
      get_leave_images(item) {
        var self = this
self.selected_leave_id = item.LeaveId

        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          UserId: user_id,
          LeaveId: item.LeaveId,
          // Count: this.page,
        }
        // GetLeaveDataRpt => UserId PersonId fDate tDate
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/GetLeaveImages', send_data, {})
          .then(response => {
            if (response.data.error == 0) {
             self.selected_leave_images = JSON.parse(response.data.items)
              self.selected_leave_images.forEach(function (val) {
                val.ImagePath = "https://"+val.ImagePath
              })
              self.goToDetail(item)
            }
          }).catch(e => {
          this.errors.push(e)
        })
      },
      infiniteHandler($state) {
        var self = this
        if (this.first) {
          // $state.loaded();
          setTimeout(() => {
            $state.loaded()
          }, 1000)
          return false
        }

        console.log('hiii' + self.last_request_items.length)
        setTimeout(() => {
          // if (self.last_request_items>0 ) {
          self.page += 1
          self.get_pre_requests($state)
          // $state.loaded();
          //
          // } else {
          //   $state.complete();
          //   console.log("completed")
          // }
        }, 1000)
      },
      open_image(img) {
        window.open(img, '_blank')
      },
      goToDetail(item) {
        if (this.disable_click) {
          return;
        }
        console.log("go to detail worked", item)
        // if (item.related_products != null) {
        this.selected_product_info = item;
        this.morakhasi_status_dialog = true;
        // }

      },
      long_click(item) {
        var self = this;
        if (item.selected == null) {
          item.selected = true
          console.log("long click worked")

        } else {
          item.selected = null
        }
        this.disable_click = true
        setTimeout(function () {
          self.disable_click = false

        }, 1000)

        if (this.request_items.filter(x => x.selected == true).length > 0) {
          this.enable_finalize = true;
        } else {
          this.enable_finalize = false;

        }
        this.$forceUpdate()
      },
      card_style(item) {
        if (item.selected) {
          return "blue";

        } else {
          return "";

        }
      },
      go_to_finalize() {
        var self = this;
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          LeaveId: self.selected_leave_id,
          StId: this.selected_status_for_change,
          Desc: this.status_summary,
          UserId: user_id,
        };

        this.axios.post('/AddLeaveStatus', send_data, {})
          .then(response => {
            const isSuccess = response.data.error == 0
            const message = response.data.msg || (isSuccess ? 'عملیات با موفقیت انجام شد' : 'خطا در ثبت وضعیت')
            bus.$emit('show_toast', [2000, message, isSuccess ? 'success' : 'error'])
            if (isSuccess) {
              self.morakhasi_status_dialog = false
              self.get_pre_requests()
              self.enable_finalize = false
            }
          }).catch(e => {
            bus.$emit('show_toast', [2000, 'خطا در ارتباط با سرور', 'error'])
            this.errors.push(e)
          })

      },
      undo () {
        this.$refs.signaturePad.clearSignature()
      },
      dataURLtoFile (dataurl, filename) {
        var arr = dataurl.split(',')
        var mime = arr[0].match(/:(.*?);/)[1]
        var bstr = atob(arr[1])
        var n = bstr.length
        var u8arr = new Uint8Array(n)

        while (n--) {
          u8arr[n] = bstr.charCodeAt(n)
        }

        return new File([u8arr], filename, { type:mime })
      },

    }
  }
</script>
<style scoped>
.morakhasi-modal-field {
  width: 100%;
  background: #ffffff;
  border: 1px solid #d1d5db !important;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 15px;
  color: #111827;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.morakhasi-modal-field:focus {
  border-color: #0d9488 !important;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.15);
}

.morakhasi-modal-select {
  min-height: 48px;
  font-weight: 500;
  appearance: none;
  cursor: pointer;
  padding-left: 40px;
}

.morakhasi-modal-textarea {
  resize: none;
  min-height: 96px;
}
</style>
<style>
  .v-application .headline {
    font-size: 1rem !important;
    font-weight: 400;
    line-height: 2rem;
    letter-spacing: normal !important;
    font-family: "IRANSans" !important;
  }
</style>
